import React from "react";
import { registerClass as postRegistration } from "../api/registrations";

export interface BreaksCampaignStats {
  students: number;
  classes: number;
  schools: number;
  breaks: number;
  minutes: number;
}

export interface ClassStats {
  breaks: number;
  minutes: number;
  studentMinutes: number;
  fullRoutine: boolean;
}

/** Fälten som anmälningsformuläret skickar in. */
export interface RegistrationForm {
  firstName: string;
  lastName: string;
  email: string;
  school: string;
  /** Skolverkets skolenhetskod. Det är den API:et identifierar skolan med. */
  schoolUnitCode: string;
  municipality: string;
  className: string;
  students: string;
  /** Honungsfälla, se SignupModal. */
  website: string;
}

export interface Registration extends Omit<
  RegistrationForm,
  "students" | "website"
> {
  students: number;
  /** Den publika klasslänken. Lärarlänken med nyckeln finns bara i mejlet. */
  link: string;
  /** Url till danssidan. Tom när sidan saknas. */
  videoLink: string;
  /** False när mejlet med lärarlänken inte kunde skickas. */
  emailSent: boolean;
}

export interface BreakReport {
  activity: string;
  duration: string | number;
  students: string | number;
}

export interface ReportedBreak extends BreakReport {
  minutes: number;
  students: number;
}

export interface BreaksCampaignContextValue {
  stats: BreaksCampaignStats;
  registration: Registration | null;
  classStats: ClassStats;
  lastReport: ReportedBreak | null;
  registerClass: (
    form: RegistrationForm,
    confirmDuplicate?: boolean,
  ) => Promise<Registration>;
  reportBreak: (report: BreakReport) => void;
  reset: () => void;
}

const EMPTY_CLASS_STATS: ClassStats = {
  breaks: 0,
  minutes: 0,
  studentMinutes: 0,
  fullRoutine: false,
};

// Startvärden för kampanjens totalsiffror. Ersätts av ett anrop mot API:et
// (GET /api/stats) när endpointen finns — därför ligger de här och inte i content.
const INITIAL_STATS: BreaksCampaignStats = {
  students: 0,
  classes: 0,
  schools: 0,
  breaks: 0,
  minutes: 0,
};

const BreaksCampaignContext =
  React.createContext<BreaksCampaignContextValue | null>(null);

export function BreaksCampaignProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [stats, setStats] = React.useState<BreaksCampaignStats>(INITIAL_STATS);
  const [registration, setRegistration] = React.useState<Registration | null>(
    null,
  );
  const [classStats, setClassStats] =
    React.useState<ClassStats>(EMPTY_CLASS_STATS);
  const [lastReport, setLastReport] = React.useState<ReportedBreak | null>(
    null,
  );

  // Anmälan av klass. Kastar RegistrationError som anroparen visar för användaren.
  const registerClass = React.useCallback(
    async (
      form: RegistrationForm,
      confirmDuplicate = false,
    ): Promise<Registration> => {
      const students = Number.parseInt(form.students, 10) || 0;

      const response = await postRegistration({
        schoolUnitCode: form.schoolUnitCode,
        className: form.className.trim(),
        studentCount: students,
        contactFirstName: form.firstName.trim(),
        contactLastName: form.lastName.trim(),
        contactEmail: form.email.trim(),
        confirmDuplicate,
        website: form.website,
      });

      const created: Registration = {
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        school: form.school,
        schoolUnitCode: form.schoolUnitCode,
        municipality: form.municipality,
        className: form.className,
        students,
        link: response.classLink,
        videoLink: response.videoLink ?? "",
        emailSent: response.emailSent,
      };

      setRegistration(created);

      // Optimistisk uppräkning av totalsiffrorna. Skolan räknas inte upp här eftersom
      // flera klasser kan anmälas från samma skola — den siffran kommer från API:et.
      setStats((prev) => ({
        ...prev,
        students: prev.students + students,
        classes: prev.classes + 1,
      }));

      return created;
    },
    [],
  );

  // Registrering av ett Break. I produktion: POST /api/breaks.
  const reportBreak = React.useCallback((report: BreakReport): void => {
    const students = Number.parseInt(String(report.students), 10) || 0;
    const minutes = Number(report.duration) || 0;
    setClassStats((prev: any) => ({
      breaks: prev.breaks + 1,
      minutes: prev.minutes + minutes,
      studentMinutes: prev.studentMinutes + minutes * students,
      fullRoutine: prev.fullRoutine || report.activity === "Hela koreografin",
    }));
    setStats((prev: any) => ({
      ...prev,
      breaks: prev.breaks + 1,
      minutes: prev.minutes + minutes * students,
    }));
    setLastReport({ ...report, minutes, students });
  }, []);

  const reset = React.useCallback((): void => {
    setStats(INITIAL_STATS);
    setRegistration(null);
    setClassStats(EMPTY_CLASS_STATS);
    setLastReport(null);
  }, []);

  const value = React.useMemo<BreaksCampaignContextValue>(
    () => ({
      stats,
      registration,
      classStats,
      lastReport,
      registerClass,
      reportBreak,
      reset,
    }),
    [
      stats,
      registration,
      classStats,
      lastReport,
      registerClass,
      reportBreak,
      reset,
    ],
  );

  return (
    <BreaksCampaignContext.Provider value={value}>
      {children}
    </BreaksCampaignContext.Provider>
  );
}

export function useBreaksCampaign(): BreaksCampaignContextValue {
  const ctx = React.useContext(BreaksCampaignContext);
  if (!ctx)
    throw new Error(
      "useBreaksCampaign måste användas inuti BreaksCampaignProvider",
    );
  return ctx;
}

export const formatNumber = (value: number): string =>
  new Intl.NumberFormat("sv-SE").format(value).replace(/\u00a0/g, " ");
